import clsx from 'clsx';
import React, { useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { useMediaQuery } from 'react-responsive';
import { Pageable } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useMassAction from '@hooks/useMassAction/useMassAction.tsx';
import { FilterSort, useFilter } from '@hooks/useFilter/Filter.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { FilterConfig, FilterUiVersionSwitch, getArrayFilterFields, getFilterFields, RingilFilterBar, useFilterUiVersion } from '@components/RingilFilters';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import { isNullOrUndefined } from '@utils/utils';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { ApiContext } from '@api/api';
import { formatBEValidationMessage } from '@hooks/useBEValidation/formatBEValidationMessage';
import { hasInspectableMassAction, MassActionConfig } from '../RingilMassActions';
import BulkActionsBar from './BulkActionsBar';
import type { PagedData } from '../RingilDataTable';
import { withoutEmptyValues } from '../withoutEmptyValues';
import { TableExportConfig, useTableExport } from '../useTableExport';
import CompactTable from './CompactTable';
import PaginatorV3 from './PaginatorV3';
import { ColumnConfigV3, headingText, isTypingTarget, visibleColumns } from './compactTableLogic';
import { buildColumnConfig, FieldDefinition } from './columns/buildColumns';
import type { FieldPreset } from './columns/columnModel';
import { fieldAccess } from './columns/fieldAccess';
import type { FieldKey, ViewSort } from './views/types';
import { DEFAULT_PAGE_SIZE } from './views/types';
import { createLocalDraftStore, createLocalViewStore } from './views/viewStore';
import { createApiViewStore, TABLE_VIEWS_ON_BACKEND } from './views/apiViewStore';
import { exportColumns } from './columns/exportColumns';
import { useTableViews } from './views/useTableViews';
import TabStrip from './views/TabStrip';
import SplitWorkspace from './split/SplitWorkspace';
import DetailPane from './split/DetailPane';
import { stepActive } from './split/splitLogic';
import { appendChunk, canLoadMore, InfiniteScrollConfig, isCapped, PagingMode, refreshRequest, resolveInfiniteScroll } from './infiniteScroll';
import LoadMoreSentinel from './LoadMoreSentinel';
import ScrollFooter from './ScrollFooter';
import usePageLayout from '@hooks/usePageLayout/usePageLayout';
import type { SplitLayout } from '@hooks/usePageLayout/pageLayout';
import ColumnSidebar from './columns/ColumnSidebar';
import SvgColumns from '@icons/grid-horizontal-inverted.svg?react';
import SvgSliders from '@icons/sliders-filled.svg?react';
import TabFiltersSidebar from './views/TabFiltersSidebar';
import { TableFilterFavouritesContext, TableFilterFavouritesContextValue } from '@components/RingilFilters/v2/tableFilterFavouritesContext';

export type { ColumnConfigV3 } from './compactTableLogic';

interface SavedFiltersResponse<TFilter> {
  filters: Array<{
    id: string;
    name: string;
    data: TFilter;
    sort?: {
      sortField: string;
      sortDirection: 'ASC' | 'DESC';
    };
  }>;
}

/**
 * Tabs ("views") for a table: each tab saves its filters, sort and columns, which are built from a
 * catalogue of fields. With this set, `columnConfig` is not used.
 */
export interface TableViewsConfig<TPagedData> {
  /** Key the tabs are stored under — the backend's `TableKeyEnum` (e.g. `EXPEDITION_LIST`). */
  tableKey: string;
  catalogue: FieldDefinition<TPagedData>[];
  presets: FieldPreset[];
  /** Columns of a new tab, as stacks of field keys. */
  defaultColumns: FieldKey[][];
  defaultSort: ViewSort;
  /** Non-configurable columns closing every row — the row actions. */
  trailing?: ColumnConfigV3<TPagedData>[];
  /** Name of the first tab a user gets ("Vše"). */
  firstViewName: string;
  /** The user's saved filters of the old list, imported as tabs after "Vše" on their first visit. */
  importLegacy?: () => Promise<{ name: string; filter: Record<string, unknown> }[]>;
  /** Sections of the column sidebar ("Obsah tabulky"); fields name theirs in `group`. */
  groups?: { key: string; fallback: string }[];
}

/** Same props as v2's RingilDataTable, so a page can swap the two behind its beta switch, plus optional tabs. */
export interface RingilDataTableV3Props<TFilter, TPagedData> {
  /** The columns of a table without tabs. Ignored when `views` is set. */
  columnConfig?: ColumnConfigV3<TPagedData>[];
  views?: TableViewsConfig<TPagedData>;
  /**
   * Split view (RD-2957 Phase 4): when the user's page layout is a split, the open row's detail shows
   * beside or below the list. Desktop only; without it, or on the list layout, the table is as before.
   */
  split?: {
    /** The detail of the open row; call `onChanged` after it saved a change so the list refreshes. */
    renderDetail: (row: TPagedData, ctx: { onChanged: () => void }) => React.ReactNode;
  };
  renderMobileCard?: (row: TPagedData, checkbox?: JSX.Element | null) => JSX.Element;
  filtersConf?: FilterConfig[];
  loadDataFromApi: (filter: TFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<TPagedData>) => void) => void;
  filterName: string;
  getSavedFilters?: (onSuccess: (data: SavedFiltersResponse<TFilter>) => void, hashAuthentication?: string, callbacks?: any) => void;
  saveFilters?: (body: { name: string; data: TFilter }, onSuccess: () => void, hashAuthentication?: string, callbacks?: any) => void;
  deleteSavedFilter?: (filterId: string, onSuccess: () => void, hashAuthentication?: string, callbacks?: any) => void;
  massActionConfig?: MassActionConfig<TPagedData>[];
  floatingMassActions?: boolean;
  rowExpansionTemplate?: (row: TPagedData, toggleExpandedRow?: (row: TPagedData) => void) => React.ReactNode;
  rowsPerPageOptions?: number[];
  hidePaginator?: boolean;
  isRowSelectable?: (row: TPagedData) => boolean;
  emptyMessage?: (filter: TFilter) => React.ReactNode;
  normalizeFilter?: (filter: TFilter) => TFilter;
  enableFilterV2?: boolean;
  exportConfig?: TableExportConfig<TFilter>;

  totalCapAt?: number;
  enableReload?: boolean;
  reloadLabel?: string;
  /**
   * Offers infinite scroll as a tab's alternative to pages ("Table settings" → Rows): `true` for
   * chunks of 50 up to 500 rows, or its own sizes. Absent: pages only. Needs `views`.
   */
  infiniteScroll?: boolean | Partial<InfiniteScrollConfig>;
  /** The controls above the table (reload, export): `icons` shows each as an icon with its name on hover, `labels` names them in text. */
  controlStyle?: 'icons' | 'labels';
}

export interface RingilDataTableV3Ref<TPagedData> {
  reloadData: () => void;
  selectedObjects: TPagedData[];
  clear: () => void;
  applyFilterValue?: (property: string, value: any) => void;
}

const rowId = (row: unknown): string | number | undefined => (row && typeof row === 'object' && 'id' in row ? (row as any).id : undefined);

/**
 * RingilDataTable v3 — the compact table of the split-view epic (RD-2957), rendered only where a page
 * opts in behind its beta surface. It is a separate component rather than a mode of v2, so v2 and its
 * ~40 consumers stay exactly as they are: data loading, filters, saved filters, mass actions and
 * export behave as in v2; the grid ({@link CompactTable}) and the paginator ({@link PaginatorV3}) are
 * new. Tabs, column configuration and the split layouts build on this in later phases.
 */
const RingilDataTableV3 = React.forwardRef(
  <TFilter, TPagedData extends { id?: string }>(
    {
      columnConfig: columnConfigProp = [],
      views,
      split,
      renderMobileCard,
      filtersConf,
      loadDataFromApi,
      filterName,
      getSavedFilters,
      saveFilters,
      deleteSavedFilter,
      massActionConfig,
      floatingMassActions = false,
      rowExpansionTemplate,
      rowsPerPageOptions = [10, 20, 50, 100],
      hidePaginator = false,
      totalCapAt,
      isRowSelectable,
      emptyMessage,
      normalizeFilter,
      exportConfig,
      enableReload = false,
      reloadLabel,
      controlStyle = 'icons',
      infiniteScroll,
      enableFilterV2 = true,
    }: RingilDataTableV3Props<TFilter, TPagedData>,
    ref?: React.Ref<RingilDataTableV3Ref<TPagedData>>,
  ) => {
    const { tr } = useTranslationLgs();

    const [loading, setLoading] = useState(false);
    const [pageData, setPageData] = useState<PagedData<TPagedData>>({ data: [], total: 0, page: 1, size: DEFAULT_PAGE_SIZE });
    const [expandedRows, setExpandedRows] = useState<TPagedData[]>([]);

    // ---- infinite scroll: the tab's mode is known only further down (tabs), so loads read it from here.
    const scrollConfig = resolveInfiniteScroll(infiniteScroll);
    const scrollRef = useRef({ mode: 'pages' as PagingMode, chunks: 0, queryKey: null as string | null, generation: 0 });
    const [loadingMore, setLoadingMore] = useState(false);

    /** Scroll mode: the first chunk of a new query, or everything loaded so far again for the same one. */
    const loadScrolled = (filter: TFilter, sort: FilterSort) => {
      const normalizedFilter = withoutEmptyValues(normalizeFilter ? normalizeFilter(filter) : filter);
      const queryKey = JSON.stringify({ normalizedFilter, sort });
      const state = scrollRef.current;
      const sameQuery = queryKey === state.queryKey && state.chunks > 0;
      const request = sameQuery ? refreshRequest(state.chunks, scrollConfig) : { pageNumber: 1, pageSize: scrollConfig.chunkSize };
      const generation = ++state.generation;
      setLoading(true);
      setLoadingMore(false);
      loadDataFromApi(normalizedFilter, { ...request, sortField: sort.sortField, sortDirection: sort.sortDirection }, data => {
        if (generation !== scrollRef.current.generation) return;
        scrollRef.current = { ...scrollRef.current, queryKey, chunks: Math.max(1, Math.ceil(request.pageSize / scrollConfig.chunkSize)) };
        setLoading(false);
        setPageData({ ...data, page: 1, size: scrollConfig.chunkSize });
      });
    };

    /** Scroll mode: the next chunk, added under the rows already shown. */
    const loadMore = () => {
      const state = scrollRef.current;
      if (state.mode !== 'scroll' || loading || loadingMore || !canLoadMore({ chunksLoaded: state.chunks, total: pageData.total, config: scrollConfig })) return;
      const normalizedFilter = withoutEmptyValues(normalizeFilter ? normalizeFilter(filter) : filter);
      const generation = state.generation;
      const pageNumber = state.chunks + 1;
      setLoadingMore(true);
      loadDataFromApi(normalizedFilter, { pageNumber, pageSize: scrollConfig.chunkSize, sortField: sort.sortField, sortDirection: sort.sortDirection }, data => {
        // A new query (filter, sort, tab) started meanwhile: this chunk belongs to the old one.
        if (generation !== scrollRef.current.generation) return;
        scrollRef.current = { ...scrollRef.current, chunks: pageNumber };
        setLoadingMore(false);
        setPageData(current => ({ ...current, total: data.total, data: appendChunk(current.data, data.data ?? [], rowId, scrollConfig.cap) }));
      });
    };

    const loadData = (pageSize: number, pageNumber: number, filter: TFilter, sort: FilterSort) => {
      if (scrollConfig && scrollRef.current.mode === 'scroll') {
        loadScrolled(filter, sort);
        return;
      }
      // A chunk still on its way is not wanted any more.
      const generation = ++scrollRef.current.generation;
      scrollRef.current.chunks = 0;
      setLoading(true);
      const normalizedFilter = withoutEmptyValues(normalizeFilter ? normalizeFilter(filter) : filter);
      loadDataFromApi(
        normalizedFilter,
        {
          pageSize: pageSize < rowsPerPageOptions[0] ? rowsPerPageOptions[0] : pageSize,
          pageNumber: pageNumber,
          sortField: sort.sortField,
          sortDirection: sort.sortDirection,
        },
        data => {
          if (generation !== scrollRef.current.generation) return;
          // A filter that shrinks the result below the current page gets an empty page back: snap to the
          // first page (which re-fetches) instead of leaving the user on an out-of-range page.
          const effectivePageSize = data.size || pageSize;
          const totalPages = Math.max(1, Math.ceil((data.total || 0) / effectivePageSize));
          if (data.total > 0 && pageNumber > totalPages) {
            applyPaging({ pageNumber: 1, pageSize: effectivePageSize });
            return;
          }
          setLoading(false);
          setPageData(data);
        },
      );
    };

    const { applyFilterValue, filter, applyPaging, page, onPage, applySorting, sort, applyFilter } = useFilter<TFilter>({
      onLoad: () => reloadData(),
      filterName: filterName,
      defaultSort:
        views?.defaultSort ??
        (() => {
          const defaultSortColumn = columnConfigProp.find(col => col.isDefaultSort);
          return { sortField: defaultSortColumn?.field ?? columnConfigProp[0]?.field, sortDirection: defaultSortColumn?.defaultSortDirection ?? 'DESC' };
        })(),
      arrayFields: getArrayFilterFields(filtersConf),
      filterKeys: getFilterFields(filtersConf),
      defaultPageSize: DEFAULT_PAGE_SIZE,
    });

    const reloadData = () => {
      loadData(page.pageSize, page.pageNumber, filter, sort);
    };

    // ---- tabs: a saved set of filters + sort + columns per tab (RD-2957). Filter and sort stay the
    // live useFilter state; the tabs read them as the working copy and push a tab's into them.
    const { loggedUser } = useContext(AuthenticatedUserContext);
    const userId = loggedUser?.id ?? 'anonymous';
    const api = useContext(ApiContext);
    const { showToastMessage } = useContext(ToastContext);
    // The store is created once per user; it reaches the API context and the toasts through this ref.
    const storeDeps = useRef({ api, showToastMessage, tr });
    storeDeps.current = { api, showToastMessage, tr };
    const viewStore = useMemo(
      () =>
        TABLE_VIEWS_ON_BACKEND
          ? createApiViewStore(() => storeDeps.current.api, {
              validation: messages => {
                const { showToastMessage: toast, tr: t } = storeDeps.current;
                toast(t('generic.error', 'Error'), messages.map(message => formatBEValidationMessage(message, t)).join('\n'), 'error');
              },
              conflict: () => {
                const { showToastMessage: toast, tr: t } = storeDeps.current;
                toast(
                  t('TableViews.changedElsewhere', 'The tab was changed elsewhere'),
                  t('TableViews.changedElsewhereDetail', 'Its latest version is loaded and your changes are kept unsaved. Save again to keep them.'),
                  'warn',
                );
              },
            })
          : createLocalViewStore(userId),
      [userId],
    );
    const draftStore = useMemo(() => createLocalDraftStore(userId), [userId]);
    const tabs = useTableViews({
      enabled: !!views,
      tableKey: views?.tableKey ?? filterName,
      userId,
      store: viewStore,
      drafts: draftStore,
      fields: views?.catalogue ?? [],
      defaultColumns: views?.defaultColumns ?? [],
      defaultSort: views?.defaultSort ?? sort,
      firstViewName: views?.firstViewName ?? '',
      importLegacy: views?.importLegacy,
      copyName: name => tr('TableViews.copyName', '{{name}} (copy)', { name }),
      live: { filter: (filter ?? {}) as Record<string, unknown>, sort, pageSize: page.pageSize },
      applyLive: content => {
        applyFilter(content.filter as TFilter);
        applySorting(content.sort.sortField, content.sort.sortDirection);
        // Another tab's rows start from its first page.
        applyPaging({ pageNumber: 1, pageSize: content.pageSize });
      },
    });

    // The tab's mode, honoured where the table offers scrolling. A switch starts the rows over.
    const pagingMode: PagingMode = scrollConfig && views ? tabs.pagingMode ?? 'pages' : 'pages';
    const previousPagingMode = useRef(pagingMode);
    scrollRef.current.mode = pagingMode;
    useEffect(() => {
      if (previousPagingMode.current === pagingMode) return;
      previousPagingMode.current = pagingMode;
      scrollRef.current.queryKey = null;
      reloadData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagingMode]);

    // "Obsah tabulky": opened from the tab menu and from the icon in the actions column's header.
    const [columnSidebarOpen, setColumnSidebarOpen] = useState(false);
    // "Výchozí nastavení filtrů": the tab menu item and the "All filters" button open this one sidebar.
    const [filtersSidebarOpen, setFiltersSidebarOpen] = useState(false);
    const columnsLabel = tr('TableViews.tableSettings', 'Table settings');
    const columnSettingsButton = (
      <RingilButton
        mode="text-only"
        icon={<SvgColumns className="w-4 h-4" />}
        tooltip={columnsLabel}
        onClick={() => setColumnSidebarOpen(true)}
        className="h-6 w-6 p-0 bg-transparent"
        dataTestId="table-columns-settings"
      />
    );

    // Built fresh on every render, so cells close over the page's current state. With tabs, the first
    // pinned-right column (the row actions) carries the column-settings icon in its header.
    const trailing = (views?.trailing ?? []).map((column, i) =>
      i === 0 && column.sticky === 'right' ? { ...column, headerRender: () => columnSettingsButton } : column,
    );
    // Without a pinned actions column there is no header to carry the icon: it goes to the bar above the table.
    const settingsInBar = !!views && !(views.trailing?.[0]?.sticky === 'right');
    const columnConfig: ColumnConfigV3<TPagedData>[] = views
      ? buildColumnConfig({ columns: tabs.displayColumns, catalogue: views.catalogue, presets: views.presets, tr, trailing })
      : columnConfigProp;

    // With tabs, anything sortable in the catalogue can order the list, not only the placed columns —
    // except fields the user may not see.
    const sortableColumns = views
      ? (() => {
          const isAllowed = fieldAccess(views.catalogue);
          return [
            ...views.catalogue
              .filter(field => field.sortField && isAllowed(field.key))
              .map(field => ({ field: field.sortField, label: tr(field.header, field.headerDefault) })),
            ...(views.trailing ?? []).filter(col => col.sortable && col.field).map(col => ({ field: col.field, label: headingText(col, tr) })),
          ];
        })()
      : columnConfig.filter(col => col.sortable && col.field).map(col => ({ field: col.field, label: headingText(col, tr) }));

    const { toggleCheckbox, isSelected, setSelectedObjects, selectedObjects, clear } = useMassAction<TPagedData>(
      (a, b) => rowId(a) === rowId(b),
      () => null,
    );

    useImperativeHandle(ref, () => ({ reloadData, selectedObjects, clear, applyFilterValue }));

    const isMobile = useMediaQuery({ maxWidth: 1024 });

    // ---- split view: the layout is the user's (profile menu), honoured on desktop when the page supports it
    const pageLayout = usePageLayout();
    const splitLayout: SplitLayout | null = split && !isMobile && pageLayout.layout !== 'list' ? pageLayout.layout : null;
    const [activeRowId, setActiveRowId] = useState<string | number | undefined>();
    // After ↓ past the last row (or ↑ before the first) the next page loads; this says which of its rows
    // opens. Scrolling, ↓ past the last row loads the next chunk, and the row at this index opens.
    const pendingEdgeRef = useRef<'first' | 'last' | number | null>(null);
    const listPaneRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!splitLayout) return;
      const rows = pageData?.data ?? [];
      if (rows.length === 0) return;
      const edge = pendingEdgeRef.current;
      pendingEdgeRef.current = null;
      if (typeof edge === 'number') setActiveRowId(rowId(rows[Math.min(edge, rows.length - 1)]));
      else if (edge) setActiveRowId(rowId(rows[edge === 'first' ? 0 : rows.length - 1]));
      // The first row opens by itself, and a row that left the page (filter, sort, paging) hands over to it.
      else if (!rows.some(row => rowId(row) === activeRowId)) setActiveRowId(rowId(rows[0]));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splitLayout, pageData]);

    const activeIndex = (pageData?.data ?? []).findIndex(row => rowId(row) === activeRowId);
    const stepArgs = (delta: -1 | 1) => ({
      index: activeIndex,
      delta,
      rowCount: pageData?.data?.length ?? 0,
      page: pageData?.page ?? 1,
      totalPages: Math.max(1, Math.ceil((pageData?.total ?? 0) / (pageData?.size || 1))),
    });
    const step = (delta: -1 | 1) => {
      const next = stepActive(stepArgs(delta));
      if (next.kind === 'none' && delta === 1 && pagingMode === 'scroll' && activeIndex === pageData.data.length - 1) {
        pendingEdgeRef.current = activeIndex + 1;
        loadMore();
        return;
      }
      if (next.kind === 'row') setActiveRowId(rowId(pageData.data[next.index]));
      if (next.kind === 'page') {
        pendingEdgeRef.current = next.edge;
        onPage({ page: next.page - 1, rows: pageData.size });
      }
    };

    const resolvedEmptyMessage = <div className={'w-full py-6 text-center text-content-secondary'}>{emptyMessage?.(filter) ?? tr('global.noRecordFound', 'No records found')}</div>;

    const { exportControl, exportShown } = useTableExport({
      exportConfig,
      filterName,
      filter,
      normalizeFilter,
      // With tabs, the export offers the open tab's columns (current view) next to the full export.
      columns: views ? () => exportColumns(tabs.columns, views.catalogue, views.defaultColumns, tr) : undefined,
      compact: true,
      iconOnly: controlStyle === 'icons',
    });

    const reloadControl = enableReload ? (
      <RingilButton
        mode="text-only"
        className={controlStyle === 'icons' ? 'h-6 w-6 p-0 bg-transparent' : 'h-6 px-1 bg-transparent text-xs'}
        label={controlStyle === 'labels' ? reloadLabel ?? tr('RingilDataTable.reload', 'Reload') : undefined}
        tooltip={controlStyle === 'icons' ? reloadLabel ?? tr('RingilDataTable.reload', 'Reload') : undefined}
        icon={<i className="pi pi-refresh" />}
        disabled={loading}
        onClick={() => reloadData()}
      />
    ) : null;

    // Separated by a thin line, like pipes.
    const controls = [settingsInBar ? columnSettingsButton : null, reloadControl, exportControl].filter(Boolean);
    const rightControls =
      controls.length > 0 ? (
        <>
          {controls.map((control, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span aria-hidden className="h-4 w-px bg-content-tertiary" />}
              {control}
            </React.Fragment>
          ))}
        </>
      ) : null;

    // Owned here (not inside RingilFilterBar) because the version switch renders below the table.
    const { version: filterUiVersion, toggle: toggleFilterUiVersion, switchVisible: filterSwitchVisible } = useFilterUiVersion();
    const { enabled: securityMode } = useSecurityMode();

    if (isNullOrUndefined(filter) || (views && !tabs.ready)) return <>Loading....</>;

    const handleSelectAllChange = (checked: boolean) => {
      setSelectedObjects(checked && pageData?.data ? (isRowSelectable ? pageData.data.filter(isRowSelectable) : pageData.data) : []);
    };

    const hasVisibleMassAction = hasInspectableMassAction(massActionConfig ?? [], selectedObjects, securityMode);

    const toggleExpandedRow = (rowData: TPagedData) => {
      setExpandedRows(prev => (prev?.some(r => r.id === rowData.id) ? prev.filter(r => r.id !== rowData.id) : [...prev, rowData]));
    };

    // With tabs, the hearted filters belong to the open tab: this provider answers the chip bar's and
    // the sidebar's useHeartedFilters with the tab's working copy instead of the user-wide store.
    const tabFavourites: TableFilterFavouritesContextValue = {
      loaded: true,
      favouritesFor: () => tabs.favouriteFilters,
      setFavourites: (_viewKey, keys) => tabs.setFavouriteFilters(keys),
      ensureLoaded: () => undefined,
    };
    const clearFilter = () => applyFilter(normalizeFilter ? normalizeFilter({} as TFilter) : ({} as TFilter));

    const content = (
      // The legacy-fields scope still styles the PrimeReact inputs of the filter bar and of the cells.
      <div className={'r-legacy-table-fields contents'}>
        {views && (
          <>
            <TabStrip
              state={tabs}
              userId={userId}
              extraMenuItems={[
                {
                  key: 'default-filters',
                  label: tr('TableViews.defaultFilters', 'Default filters'),
                  icon: <SvgSliders />,
                  onClick: () => setFiltersSidebarOpen(true),
                  dataTestId: 'table-view-menu-filters',
                },
                { key: 'table-settings', label: columnsLabel, icon: <SvgColumns />, onClick: () => setColumnSidebarOpen(true), dataTestId: 'table-view-menu-columns' },
              ]}
            />
            {filtersConf?.length > 0 && (
              <TabFiltersSidebar
                visible={filtersSidebarOpen}
                onClose={() => setFiltersSidebarOpen(false)}
                viewName={tabs.activeView?.name ?? ''}
                filterName={filterName}
                filtersConf={filtersConf}
                filter={filter}
                applyFilterValue={applyFilterValue}
                loading={loading}
                onReset={clearFilter}
                // Only the owner saves a tab; on a colleague's shared one the sidebar just filters.
                onSave={tabs.activeView && tabs.isOwn(tabs.activeView) ? () => tabs.actions.saveFilters() : undefined}
                sort={sort}
                applySorting={applySorting}
                sortableColumns={sortableColumns}
              />
            )}
            <ColumnSidebar
              visible={columnSidebarOpen}
              onClose={() => setColumnSidebarOpen(false)}
              columns={tabs.columns}
              onApply={tabs.setColumns}
              defaultColumns={tabs.defaultColumns}
              paging={scrollConfig ? { mode: pagingMode, cap: scrollConfig.cap, onApply: tabs.setPagingMode } : undefined}
              fields={views.catalogue}
              presets={views.presets}
              // Without configured sections, one per distinct field group, in catalogue order.
              groups={views.groups ?? Array.from(new Set(views.catalogue.map(field => field.group))).map(group => ({ key: group, fallback: group }))}
            />
          </>
        )}
        <RingilFilterBar
          filtersConf={filtersConf}
          filterName={filterName}
          filter={filter}
          applyFilterValue={applyFilterValue}
          applyFilter={applyFilter}
          loading={loading}
          sort={sort}
          applySorting={applySorting}
          sortableColumns={sortableColumns}
          // Tabs replace saved filters, and a table with tabs uses the chip bar only.
          getSavedFilters={views ? undefined : getSavedFilters}
          saveFilters={views ? undefined : saveFilters}
          deleteSavedFilter={views ? undefined : deleteSavedFilter}
          normalizeFilter={normalizeFilter}
          enableFilterV2={views ? true : enableFilterV2}
          filterUiVersion={views ? 'v2' : filterUiVersion}
          variant="compact"
          onOpenAllFilters={views ? () => setFiltersSidebarOpen(true) : undefined}
        />
        {pageData && (
          <div>
            {/* The table's frame (Figma 121:7402): one 1px border around the bulk bar, header and rows. */}
            <div className={clsx(!(isMobile && renderMobileCard) && 'border border-solid border-content-quaternary')}>
              {(hasVisibleMassAction || exportShown || enableReload || settingsInBar) && (
                <BulkActionsBar
                  massActionConfig={massActionConfig ?? []}
                  selectedObjects={selectedObjects}
                  idPrefix={filterName}
                  hasMassActions={hasVisibleMassAction}
                  rightSlot={rightControls}
                />
              )}

              {isMobile && renderMobileCard ? (
                <div className="mobile-cards-container">
                  {pageData.data.length === 0 && resolvedEmptyMessage}
                  {pageData.data.map((row, index) => (
                    <div key={index} className="mobile-card-wrapper">
                      <div className="mobile-card-content-with-checkbox">
                        <div className="mobile-card-content">
                          {renderMobileCard(
                            row,
                            hasVisibleMassAction && (!isRowSelectable || isRowSelectable(row)) ? (
                              <Checkbox checked={isSelected(row)} onChange={() => toggleCheckbox(row)} className="mobile-card-injected-checkbox" />
                            ) : null,
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <CompactTable
                  rows={pageData.data}
                  columns={visibleColumns(columnConfig)}
                  rowKey={(row, index) => rowId(row) ?? index}
                  sort={sort}
                  onSort={next => applySorting(next.sortField, next.sortDirection)}
                  loading={loading}
                  emptyMessage={resolvedEmptyMessage}
                  showSelection={hasVisibleMassAction}
                  isRowSelectable={isRowSelectable}
                  isSelected={isSelected}
                  onToggleSelected={toggleCheckbox}
                  onToggleAll={handleSelectAllChange}
                  // With tabs, a dragged header edge sets the column's width in the tab's working copy.
                  onResizeColumn={views ? tabs.resizeColumn : undefined}
                  isExpanded={row => expandedRows.some(r => r.id === row.id)}
                  toggleExpandedRow={toggleExpandedRow}
                  // In the split layouts an expand trigger (e.g. the AI badge) both expands its row and opens it in the pane.
                  rowExpansionTemplate={rowExpansionTemplate}
                  activeRowKey={splitLayout ? activeRowId : undefined}
                  onRowClick={splitLayout ? row => setActiveRowId(rowId(row)) : undefined}
                />
              )}
            </div>
            {pagingMode === 'scroll' ? (
              <>
                {canLoadMore({ chunksLoaded: scrollRef.current.chunks, total: pageData.total, config: scrollConfig }) && (
                  // A fresh one after every chunk, so a short chunk that leaves it in reach asks again.
                  <LoadMoreSentinel key={pageData.data.length} onNear={loadMore} />
                )}
                <ScrollFooter
                  loaded={pageData.data.length}
                  total={pageData.total}
                  loadingMore={loadingMore}
                  capped={isCapped({ loaded: pageData.data.length, total: pageData.total, config: scrollConfig })}
                  totalCapAt={totalCapAt}
                />
              </>
            ) : (
              !hidePaginator && (
                <PaginatorV3
                  totalRecords={pageData.total}
                  page={pageData.page}
                  pageSize={pageData.size}
                  rowsPerPageOptions={rowsPerPageOptions}
                  totalCapAt={totalCapAt}
                  onPage={onPage}
                  isMobile={isMobile}
                />
              )
            )}
          </div>
        )}
        {!views && enableFilterV2 && filtersConf?.length > 0 && filterSwitchVisible && (
          // v3 lives behind its own table switch, so this link has to say it only swaps the filters.
          <FilterUiVersionSwitch
            version={filterUiVersion}
            onToggle={toggleFilterUiVersion}
            labels={{
              toNew: tr('RingilDataTable.switchToNewFilters', 'Switch to new filters'),
              toLegacy: tr('RingilDataTable.switchToLegacyFilters', 'Switch to legacy filters'),
            }}
          />
        )}
      </div>
    );

    const activeRow = splitLayout ? pageData?.data?.find(row => rowId(row) === activeRowId) : undefined;
    const body = splitLayout ? (
      <SplitWorkspace
        layout={splitLayout}
        ratio={pageLayout.ratio[splitLayout]}
        onRatioChange={next => pageLayout.setRatio(splitLayout, next)}
        list={
          // ↑/↓ move the open row while the list has focus — but not while typing into a filter.
          <div
            ref={listPaneRef}
            tabIndex={-1}
            className="outline-none"
            onKeyDown={event => {
              if (isTypingTarget(event.target as HTMLElement) || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
              event.preventDefault();
              step(event.key === 'ArrowDown' ? 1 : -1);
            }}
          >
            {content}
          </div>
        }
        detail={
          activeRow ? (
            <DetailPane onEscape={() => (listPaneRef.current?.querySelector<HTMLElement>('[data-compact-table]') ?? listPaneRef.current)?.focus()}>
              {split.renderDetail(activeRow, { onChanged: () => reloadData() })}
            </DetailPane>
          ) : (
            <div className="py-12 text-center text-content-secondary">{resolvedEmptyMessage}</div>
          )
        }
      />
    ) : (
      content
    );

    return views ? <TableFilterFavouritesContext.Provider value={tabFavourites}>{body}</TableFilterFavouritesContext.Provider> : body;
  },
);

RingilDataTableV3.displayName = 'RingilDataTableV3';

export default RingilDataTableV3 as <TFilter, TPagedData>(
  props: RingilDataTableV3Props<TFilter, TPagedData> & { ref?: React.Ref<RingilDataTableV3Ref<TPagedData>> },
) => JSX.Element;
